import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Clock, ArrowRight, Plane, Stethoscope, Coffee, CalendarDays } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { PageHeader, ui } from "../../../components/ui";
import sessionDB from "../../../utils/sessionDB";
import { leaveService } from "../../../services/leaveService";

const LEAVE_ICONS = {
  AL: { icon: Plane, tone: "var(--color-accent)" },
  EL: { icon: CalendarDays, tone: "var(--color-accent)" },
  SL: { icon: Stethoscope, tone: "var(--color-warning)" },
  CL: { icon: Coffee, tone: "var(--color-success)" }
};

function getLeaveIcon(leaveCode, index) {
  const style = LEAVE_ICONS[leaveCode?.toUpperCase()];
  if (style) return style;

  const fallback = [Plane, Stethoscope, Coffee, CalendarDays];
  const tones = ["var(--color-accent)", "var(--color-warning)", "var(--color-success)", "var(--color-primary)"];
  return {
    icon: fallback[index % fallback.length],
    tone: tones[index % tones.length]
  };
}

export function ManagerDashboard() {
  const navigate = useNavigate();

  return (
    <AppLayout>
          <PageHeader
        title={i18n.t("leave.leaveManagement")}
        action={
        <button
          onClick={() => navigate("/hrms/apply-leave")}
          className={ui.primaryBtn}>
          
                <Plus className="h-4 w-4" />
                <Trans i18nKey="formActions.applyLeave" />
              </button>
        } />
      

          {/* Two primary cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LeaveSummaryCard />
            <PendingRequestsCard />
          </div>
    </AppLayout>);

}

function LeaveSummaryCard() {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sessionEmpId = Number(sessionDB.getEmpId());
    const optionalEmpId =
    Number.isFinite(sessionEmpId) && sessionEmpId > 0 ? sessionEmpId : undefined;

    leaveService
    .getLeaveBalance(optionalEmpId)
    .then(setBalances)
    .catch((err) => {
      console.error("Failed to load leave balances", err);
      setBalances([]);
    })
    .finally(() => setLoading(false));
  }, []);

  const summaryItems = balances
  .filter((entry) => entry.total != null && entry.total > 0)
  .map((entry, index) => {
    const { icon: Icon, tone } = getLeaveIcon(entry.leaveCode, index);
    const used = entry.leavesApproved ?? entry.used ?? 0;

    return {
      key: entry.leaveTypeId ?? entry.leaveName ?? index,
      label: entry.leaveName || "Leave",
      used,
      total: entry.total,
      icon: Icon,
      tone
    };
  });

  return (
    <div className={`${ui.card} p-6 sm:p-7 flex flex-col`}>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-[18px] font-semibold text-foreground"><Trans i18nKey="leave.leaveSummary" /></h2>
          <p className="text-xs per-file-hint mt-0.5"><Trans i18nKey="leave.yourPersonalLeaveBalances" /></p>
        </div>
      </div>
      {loading ?
      <p className="text-sm per-file-hint"><Trans i18nKey="leave.loadingLeaveBalances" /></p> :
      summaryItems.length === 0 ?
      <p className="text-sm per-file-hint"><Trans i18nKey="leave.noLeaveBalancesAvailable" /></p> :

      <ul className="space-y-5 flex-1">
          {summaryItems.map((b) => {
          const pct = b.total > 0 ? Math.round(b.used / b.total * 100) : 0;
          return (
            <li key={b.key}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div
                    className="h-9 w-9 rounded-lg grid place-items-center"
                    style={{ backgroundColor: `color-mix(in oklab, ${b.tone} 12%, transparent)` }}>
                    
                      <b.icon className="h-[18px] w-[18px]" style={{ color: b.tone }} />
                    </div>
                    <span className="text-sm font-semibold text-foreground">{b.label}</span>
                  </div>
                  <span className="text-sm font-semibold text-foreground">
                    {b.used.toFixed(1)} / {b.total.toFixed(1)}{" "}
                    <span className="per-file-hint font-medium"><Trans i18nKey="dateTime.days" /></span>
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-workspace/80 overflow-hidden">
                  <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, backgroundColor: b.tone }} />
                
                </div>
              </li>);

        })}
        </ul>
      }
    </div>);

}

function PendingRequestsCard() {
  return (
    <div className={`${ui.card} p-6 sm:p-7 flex flex-col`}>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-[18px] font-semibold text-foreground"><Trans i18nKey="leave.pendingRequests" /></h2>
          <p className="text-xs per-file-hint mt-0.5"><Trans i18nKey="leave.employeeLeaveRequestsToReview" /></p>
        </div>
        <div className="h-10 w-10 rounded-xl bg-warning/12 grid place-items-center">
          <Clock className="h-5 w-5 text-warning" />
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center items-start py-2">
        <div className="flex items-baseline gap-2">
          <span className="text-[56px] leading-none font-bold text-foreground">8</span>
          <span className="text-base font-semibold per-file-hint"><Trans i18nKey="formActions.requests" /></span>
        </div>
        <p className="text-sm per-file-hint mt-2"><Trans i18nKey="leave.awaitingYourApproval" /></p>
      </div>

      <Link
        to="/hrms/manager/leave-requests"
        className={`${ui.primaryBtn} mt-5 h-12`}>
        <Trans i18nKey="formActions.viewRequests" />
        
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>);

}
